import React, { useState, useEffect, useRef } from 'react';
import './Dimmer.css';

function Dimmer({device, setDeviceValue, max}) {
	const [value, setValue] = useState(device.value);
	const [isInteracting, setIsInteracting] = useState(false);

	const throttleTimer = useRef(null);
	const latestValue = useRef(value);
	const interactionEndTimer = useRef(null);

	// Sync with server state when not interacting
	useEffect(() => {
		if (!isInteracting) {
			setValue(device.value);
		}
	}, [device.value, isInteracting]);

	// Effect to handle throttled slider value changes
	useEffect(() => {
		latestValue.current = value;

		if (isInteracting) {
			if (!throttleTimer.current) {
				setDeviceValue(device.serial, value);
				throttleTimer.current = setTimeout(() => {
					throttleTimer.current = null;
					// After timeout, if value changed during cooldown, send the latest value
					if (latestValue.current !== value) {
						setDeviceValue(device.serial, latestValue.current);
					}
				}, 100);
			}
		}
	}, [value, isInteracting, device.serial, setDeviceValue]);

	// Cleanup on unmount
	useEffect(() => {
		return () => {
			if (throttleTimer.current) {
				clearTimeout(throttleTimer.current);
			}
			if (interactionEndTimer.current) {
				clearTimeout(interactionEndTimer.current);
			}
		};
	}, []);

	const handleChange = (e) => {
		setValue(e.target.valueAsNumber);
	};

	const handleInteractionStart = () => {
		if (interactionEndTimer.current) {
			clearTimeout(interactionEndTimer.current);
			interactionEndTimer.current = null;
		}
		setIsInteracting(true);
	};

	const handleInteractionEnd = () => {
		// When interaction ends, ensure the final value is sent.
		clearTimeout(throttleTimer.current);
		throttleTimer.current = null;
		if (latestValue.current !== device.value) {
			setDeviceValue(device.serial, latestValue.current);
		}

		interactionEndTimer.current = setTimeout(() => {
			setIsInteracting(false);
			interactionEndTimer.current = null;
		}, 250);
	};

	const handleButtonClick = (newValue) => {
		setValue(newValue);
		setDeviceValue(device.serial, newValue);
	};

	return (
		<div className='dimmer'>
			<div className='left'>
				<button onClick={() => handleButtonClick(0)}>⚫️</button>
			</div>

			<div className='middle'>
				<input
					type='range'
					min='0'
					max={max}
					value={value}
					onChange={handleChange}
					onMouseDown={handleInteractionStart}
					onMouseUp={handleInteractionEnd}
					onTouchStart={handleInteractionStart}
					onTouchEnd={handleInteractionEnd}
				/>

				<div className='label'>
					{device.name}
				</div>
			</div>

			<div className='right'>
				<button onClick={() => handleButtonClick(max)}>🟡</button>
			</div>
		</div>
	);
}

export default Dimmer;
